import clsx from "clsx";
import { InfoIcon, MessageSquareIcon } from "lucide-react";
import { useRef } from "react";

import { AgenticStaffingStatus } from "./AgenticStaffingStatus";
import { Column } from "@/components/Column";
import { Row } from "@/components/Row";
import { Text } from "@/components/Text";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import { textAIOutreachStatusToLabel } from "@/lib/constants";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { TextAIOutreachStatus, TextAIOutreachWithProxy } from "@/types";

const BaseTextAIOutreachInformation = ({
  status,
}: {
  status: TextAIOutreachStatus;
}) => {
  return (
    <Row className="items-center gap-2">
      <MessageSquareIcon
        className={clsx("h-4 w-4", {
          "text-green-600":
            status === TextAIOutreachStatus.ACTIVE ||
            status === TextAIOutreachStatus.INITIATED ||
            status === TextAIOutreachStatus.CALL_COMPLETED,
          "text-blue-500":
            status === TextAIOutreachStatus.CALL_SCHEDULED ||
            status === TextAIOutreachStatus.CALL_RESCHEDULED,
          "text-yellow-600": status === TextAIOutreachStatus.HOLD,
          "text-red-500": status === TextAIOutreachStatus.STOP,
        })}
      />
      <Text>{textAIOutreachStatusToLabel[status]}</Text>
    </Row>
  );
};

const ProxyLink = ({ proxy }: { proxy: TextAIOutreachWithProxy["proxy"] }) => {
  if (!proxy) {
    return null;
  }

  return (
    <Button
      className="h-auto p-0"
      onClick={() => {
        mixpanelUtils.trackButtonClick("Proxy link", MIXPANEL_SCREENS.JOB);
        window.open(
          // TODO: Use a central source of truth for routes.
          `/companies/${proxy.company.id}/jobs/${proxy.job.id}?c=${proxy.candidacy.id}`,
          "_blank",
        );
      }}
      variant="link"
    >
      {proxy.job.title}: {proxy.company.name}
    </Button>
  );
};

export const TextAIOutreachInformation = ({
  textAIOutreachWithProxy,
}: {
  textAIOutreachWithProxy: TextAIOutreachWithProxy;
}) => {
  const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  if (textAIOutreachWithProxy.proxy === null) {
    return (
      <BaseTextAIOutreachInformation status={textAIOutreachWithProxy.status} />
    );
  }

  return (
    <Row className="items-center gap-2">
      <div className="rounded-full border border-purple-200 bg-purple-50 px-1.5 py-0.5">
        <p className="text-xs font-medium text-purple-700">Proxy</p>
      </div>
      <TooltipProvider delayDuration={0}>
        <Tooltip>
          <TooltipTrigger
            onMouseEnter={() => {
              hoverTimerRef.current = setTimeout(() => {
                mixpanelUtils.trackHover(
                  "Proxy info tooltip",
                  MIXPANEL_SCREENS.JOB,
                );
              }, 300);
            }}
            onMouseLeave={() => {
              if (hoverTimerRef.current) {
                clearTimeout(hoverTimerRef.current);
                hoverTimerRef.current = null;
              }
            }}
          >
            <InfoIcon className="h-4 w-4 text-slate-400" />
          </TooltipTrigger>
          <TooltipContent side="top">
            <Column className="items-start gap-2">
              <p>
                This candidate was contacted by Stevie for another job
                application in the same window.
              </p>
              <AgenticStaffingStatus
                textAIOutreachWithProxy={{
                  id: textAIOutreachWithProxy.proxyTextAIOutreachId ?? "",
                  ...textAIOutreachWithProxy.proxy.textAIOutreach,
                  proxy: null,
                }}
                voiceAIOutreachSlim={
                  textAIOutreachWithProxy.proxy.voiceAIOutreachSlim
                }
              />
              <ProxyLink proxy={textAIOutreachWithProxy.proxy} />
            </Column>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </Row>
  );
};
